'use client'

import { FileText, Globe } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface SidebarAioReport {
  id: string
  topic: string
  website_url?: string
  full_report_available: boolean
}

const getAioReportSelectLabel = (report: Pick<SidebarAioReport, 'website_url' | 'topic'>) => {
  if (!report.website_url) return report.topic
  try {
    const parsed = new URL(report.website_url)
    const host = parsed.hostname.replace(/^www\./, '')
    const path = parsed.pathname.replace(/\/+$/, '')
    return path ? `${host}${path}` : host
  } catch {
    return report.website_url
  }
}

const SidebarAioReportSelectItem = ({ report }: { report: SidebarAioReport }) => (
  <SelectItem value={report.id}>
    <div className="flex min-w-0 items-center gap-2">
      {report.website_url ? (
        <Globe className="h-3 w-3 flex-shrink-0 text-blue-500" aria-hidden />
      ) : (
        <FileText className="h-3 w-3 flex-shrink-0 text-green-500" aria-hidden />
      )}
      <span className="truncate">{getAioReportSelectLabel(report)}</span>
      {report.full_report_available ? (
        <Badge variant="secondary" className="ml-1 flex-shrink-0 text-xs">
          Available
        </Badge>
      ) : null}
    </div>
  </SelectItem>
)

const SidebarAioReportSelectGroup = ({
  label,
  reports,
}: {
  label: string
  reports: SidebarAioReport[]
}) => {
  if (reports.length === 0) return null

  return (
    <SelectGroup>
      <SelectLabel className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
        {label}
      </SelectLabel>
      {reports.map((report) => (
        <SidebarAioReportSelectItem key={report.id} report={report} />
      ))}
    </SelectGroup>
  )
}

export const SidebarAioReportSwitcher = ({
  currentReportId,
  ownedReports,
  sharedReports,
  isLoading,
  triggerClassName,
  onSelectReport,
}: {
  currentReportId: string | null
  ownedReports: SidebarAioReport[]
  sharedReports: SidebarAioReport[]
  isLoading: boolean
  triggerClassName: string
  onSelectReport: (reportId: string) => void
}) => {
  const selectedReport =
    ownedReports.find((report) => report.id === currentReportId) ??
    sharedReports.find((report) => report.id === currentReportId)
  const hasReports = ownedReports.length > 0 || sharedReports.length > 0

  const handleValueChange = (value: string) => {
    if (value) onSelectReport(value)
  }

  return (
    <Select value={selectedReport ? selectedReport.id : ''} onValueChange={handleValueChange}>
      <SelectTrigger className={triggerClassName} aria-label="AIO reports">
        <SelectValue placeholder="AIO Reports">
          {selectedReport ? getAioReportSelectLabel(selectedReport) : 'AIO Reports'}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 px-2 py-2" aria-busy="true" aria-label="Loading reports">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-9 w-full rounded-md" />
            ))}
          </div>
        ) : hasReports ? (
          <>
            <SidebarAioReportSelectGroup label="Your reports" reports={ownedReports} />
            <SidebarAioReportSelectGroup label="Shared with you" reports={sharedReports} />
          </>
        ) : (
          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
            No reports yet
          </div>
        )}
      </SelectContent>
    </Select>
  )
}
